import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import FACEBOOK from '@/assets/images/account/facebook.svg';
import GOOGLE from '@/assets/images/account/google.svg';
import WECHAT from '@/assets/images/account/wechat.svg';

import Constants from '@/constants/const';
import { facebookLogin } from '@/api/customer';
import { Toast } from '@/components/common/CommonToast';
import { WeeeIcon } from '@/components/common/Icon';
import { hideLoading, showLoading } from '@/components/common/Indicator/pageIndicator';
import useClickActionT2 from '@/tracks/useClickActionT2';
import { getInviterParams, getReferralIdFromCookie } from '@/utils/common/referral';
import EventEmitter, { actions } from '@/utils/eventemitter';
import { loadFacebookSdk } from '@/utils/facebook';
import logger from '@/utils/logger';

import AccountDeletedContent from '@/components/account/AccountDeletedContent';
import GoogleOauthLogin from '@/components/common/GoogleOauthLogin';
import Popup from '@/components/common/Popup';
import { LoginMethod } from '@/types/account.d';
import { EnvelopeOutline, PhoneFilled } from '@weee-fe/kunlunicon';
import {
  isFacebookBrowser,
  isFBBrowser,
  isInstagramBrowser,
  isMobileBrowser,
  isTiktokBrowser,
  isWeixinBrowser
} from '@/utils/common/device';
import {
  afterUserLogin,
  afterUserLoginToOnboarding,
  afterUserRegisterLogin,
  afterUserSign
} from '@/utils/common/pixel';
import { getUniqueId } from '@/utils/common/uniqueId';
import { addParameterToUrl, buildUrlWithOrigin } from '@/utils/url';
import { twMerge } from 'tailwind-merge';
import { isLatinoChannel } from '@/utils/common/latino-app';
import { getSafeRedirectUrl } from '@/utils/safeRedirect';
import {
  ACCOUNT_AUTH_FB_SOURCE_STORAGE_KEY,
  ACCOUNT_AUTH_FB_SOURCE_FLOW_KEY,
  getAccountAuthSource
} from '@/utils/accountAuthSource';

const itemBaseClass =
  'inline-flex shrink-0 flex-nowrap items-center gap-2 py-2 px-2.5 rounded-full border border-solid border-surface-100-hairline enki-elevation-2 enki-body-sm-medium text-surface-100-fg-default whitespace-nowrap cursor-pointer needsclick';

type ThirdLoginProps = {
  className?: string;
  trackType?: string;
  handleClickTrack?: (trackType: string, clickType: string) => void;
  clickTrack?: (type: string) => void;
  onTabClick?: (tab: number) => void;
  /**登录注册弹框成功回调 */
  onThirdLoginSuccess?: (arg?: any, res?: any) => void;
  currentTab?: number;
  isLoginSignupPopup?: boolean;
  wechatPopUpSuccessUrl?: string;
  redirect?: string;
  showNewStyle?: boolean;
  tipsText?: string | React.ReactNode;
  activeLoginMethod: 'email' | 'phone';
  /** 切换邮箱/手机登录方式 */
  onLoginMethodSwitch?: () => void;
};

const ChannelWidget = props => {
  const { className, handleClick, children, ...rest } = props;
  return (
    <div
      data-testid="wid-third-login-item"
      className={twMerge(
        classNames(
          'w-[43px] h-[43px] p-[10px] rounded-full inline-flex justify-center items-center last-of-type:mr-0',
          className,
          'needsclick'
        )
      )}
      onClick={handleClick}
      {...rest}>
      {children}
    </div>
  );
};

const ThirdLoginV3 = (props: ThirdLoginProps) => {
  const {
    handleClickTrack,
    trackType,
    clickTrack,
    onThirdLoginSuccess,
    wechatPopUpSuccessUrl,
    isLoginSignupPopup,
    className,
    activeLoginMethod,
    onLoginMethodSwitch
  } = props;

  const [showWXLogin, setShowWXLogin] = useState(false);
  const [showFbLogin, setShowFbLogin] = useState(false);
  //fackbook 授权登录, 在facebook app中会使用授权的方式去登录
  const [showFBAuthLogin, setShowFBAuthLogin] = useState(false);
  const [showDeletedPopup, setShowDeletedPopup] = useState<boolean>(false);
  const isTiktok = isTiktokBrowser();
  const isPc = !isMobileBrowser();
  const [showGoogleOauthLogin, setShowGoogleOauthLogin] = useState(false);

  const router = useRouter();
  const { t: tCommon, lang } = useTranslation('common');
  const { buttonClick } = useClickActionT2();
  const isLatino = isLatinoChannel();
  const isTh = lang === Constants.LANGUAGE_TH;

  useEffect(() => {
    setShowWXLogin(!isTh && (isWeixinBrowser() || isPc));
    const showFbAuth = isFacebookBrowser();
    setShowFBAuthLogin(showFbAuth);
    setShowFbLogin(!isWeixinBrowser() && !isTiktok && !showFbAuth);
    setShowGoogleOauthLogin(!isWeixinBrowser());
  }, [isTiktok, isPc, isLatino, isTh]);

  const redirectUrl = props?.redirect ? props.redirect : getSafeRedirectUrl(router.query?.redirect_url as string);
  const forceMethod = router.query?.force_method as string;
  const accountAuthSource = getAccountAuthSource(router.query?.source, router.pathname);

  useEffect(() => {
    if (showFbLogin) {
      loadFacebookSdk().then(res => {
        if (res && forceMethod === LoginMethod.FACEBOOK) {
          handleFbLogin(res);
        }
      });
      return;
    }
    if (showFBAuthLogin && forceMethod === LoginMethod.FACEBOOK) {
      handleFbAuthLogin();
      return;
    }
  }, [showFbLogin, showFBAuthLogin, forceMethod]);

  const onSuccess = (isLoginSignUpPopUp = false, lang, redicrectUrl, isBind, isPhoneABTestBind, thirdLoginType) => {
    // fb 登录验证的弹框关闭后 wechat跳到邮箱绑定页，fb是展示bind-connect弹框，把bind-connect和siggnup写成一个弹框
    if (!isPhoneABTestBind) {
      if (isLoginSignUpPopUp) {
        EventEmitter.emit(actions.showPhoneConnectPopUp);
        onThirdLoginSuccess?.();
        return;
      }
      const reirectUrl = `${
        process.env.NEXT_PUBLIC_WEB_HOST
      }/${lang}/account/phone-connect?channel_type=phone_verify&redirect_url=${encodeURIComponent(
        redicrectUrl
      )}&type=${thirdLoginType}`;
      window.location.href = reirectUrl;
      return;
    }
    if (isBind) {
      if (isLoginSignUpPopUp) {
        EventEmitter.emit(actions.showBindConnetPopUp);
        onThirdLoginSuccess?.();
        return;
      }
      const reirectUrl = `${
        process.env.NEXT_PUBLIC_WEB_HOST
      }/${lang}/account/bind-connect?redirect_url=${encodeURIComponent(redicrectUrl)}&type=${thirdLoginType}`;
      window.location.href = reirectUrl;
      return;
    }
    if (isLoginSignUpPopUp) {
      onThirdLoginSuccess?.();
      router.reload();
      return;
    }
    onThirdLoginSuccess?.({}, { is_binded: isPhoneABTestBind, is_need_bind: isBind });
  };

  const handleGoogleClick = () => {
    handleClickTrack?.(trackType, 'google');
    if (clickTrack) {
      clickTrack?.('gg');
      return;
    }
    buttonClick({
      co: {
        target_nm: 'google'
      }
    });
  };

  const handleWechatLogin = () => {
    handleClickTrack?.(trackType, 'wechat');
    if (clickTrack) {
      clickTrack?.('wx');
    } else {
      buttonClick({
        co: {
          target_nm: 'wechat'
        }
      });
    }

    const redirectLink = wechatPopUpSuccessUrl || (redirectUrl as string);
    const sourceQuery = accountAuthSource ? `&source=${encodeURIComponent(accountAuthSource)}` : '';
    window.location.href = buildUrlWithOrigin(
      `/${lang}/account/login/wechat?redirect_url=${encodeURIComponent(
        isLoginSignupPopup ? addParameterToUrl(redirectLink, 'isLoginSignupPopup', '1') : redirectLink
      )}${sourceQuery}`
    );
  };

  const handleFbLogin = async (res?) => {
    handleClickTrack?.(trackType, 'facebook');
    if (clickTrack) {
      clickTrack?.('fb');
    } else {
      buttonClick({
        co: {
          target_nm: 'facebook'
        }
      });
    }

    try {
      const sdk = !!res ? res : await loadFacebookSdk();
      if (sdk) {
        window.FB.login(
          response => {
            console.log('response----', response);
            if (response.status === 'connected') {
              window.FB.api(
                '/me',
                { fields: 'id, name, first_name, last_name, picture.type(large), email' },
                async userInfo => {
                  try {
                    showLoading();
                    const res = await facebookLogin({
                      user_info: JSON.stringify(userInfo),
                      referral_id: getReferralIdFromCookie(),
                      ...(accountAuthSource ? { source: accountAuthSource } : {}),
                      ...(redirectUrl ? { page_url: redirectUrl } : {}),
                      ...getInviterParams()
                    });
                    hideLoading();
                    if (res?.result) {
                      const isBind = res?.object?.is_need_bind || 0;
                      const isPhoneABTestBind = res?.object?.is_binded ?? true;
                      afterUserRegisterLogin(res?.object?.token, res?.object?.language);
                      if (res?.object?.is_sign_up) {
                        afterUserSign('facebook');
                      } else {
                        afterUserLogin('facebook');
                      }
                      // 用户注册、登录成功之后，优先根据next_url跳转
                      await afterUserLoginToOnboarding(res?.object?.next_url, redirectUrl);

                      onSuccess(isLoginSignupPopup, lang, redirectUrl, isBind, isPhoneABTestBind, 'facebook');
                    } else {
                      Toast({
                        text: res?.message,
                        duration: 3000,
                        showIcon: false
                      });
                    }
                  } catch (err) {
                    console.log('catch2----', err);
                    logger.error({
                      page: window.location.href,
                      method: 'fb login /me',
                      err
                    });
                  }
                }
              );
            }
          },
          {
            scope: 'public_profile,email'
          }
        );
      }
    } catch (err) {
      console.log('catch----', err);
      logger.error({
        page: window.location.href,
        method: handleFbLogin.name,
        err
      });
    }
  };

  const handleFbAuthLogin = () => {
    sessionStorage.setItem('fbAuthLoginSuccessRedirectUrl', redirectUrl as string);
    if (accountAuthSource) {
      sessionStorage.setItem(ACCOUNT_AUTH_FB_SOURCE_STORAGE_KEY, accountAuthSource);
      sessionStorage.setItem(ACCOUNT_AUTH_FB_SOURCE_FLOW_KEY, '1');
    } else {
      sessionStorage.removeItem(ACCOUNT_AUTH_FB_SOURCE_STORAGE_KEY);
      sessionStorage.removeItem(ACCOUNT_AUTH_FB_SOURCE_FLOW_KEY);
    }
    const redirectUri = `${process.env.NEXT_PUBLIC_WEB_HOST}/en/account/auth/fb`;
    window.location.href = `https://m.facebook.com/v15.0/dialog/oauth?client_id=${
      process.env.NEXT_PUBLIC_FB_CLIENT_ID
    }&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&response_type=code&state=${getUniqueId()}&scope=public_profile,email`;
  };

  const thirdMethods = [
    {
      rowKey: 'third-login-google',
      show: showGoogleOauthLogin,
      iconImg: GOOGLE,
      name: 'Google',
      text: tCommon('continue_v3_google'),
      icon: <WeeeIcon type="icongoogle" className="text-reserved-true-white !text-[28px] !leading-none" />,
      iconClass: 'bg-[#6580E0]'
    },
    {
      rowKey: 'third-login-facebook-sdk',
      show: showFbLogin,
      iconImg: FACEBOOK,
      name: 'Facebook',
      onClick: () => handleFbLogin(),
      text: tCommon('continue_v3_facebook'),
      icon: <WeeeIcon type="iconfb" className="text-reserved-true-white !text-[30px] !leading-none" />,
      iconClass: 'bg-[#49568d]'
    },
    {
      rowKey: 'third-login-facebook-auth',
      show: showFBAuthLogin,
      iconImg: FACEBOOK,
      name: 'Facebook',
      onClick: () => handleFbAuthLogin(),
      text: tCommon('continue_v3_facebook'),
      icon: <WeeeIcon type="iconfb" className="text-reserved-true-white !text-[30px] !leading-none" />,
      iconClass: 'bg-[#49568d]'
    },
    {
      rowKey: 'third-login-wechat',
      show: !isLatino && showWXLogin,
      iconImg: WECHAT,
      name: 'Wechat',
      onClick: () => handleWechatLogin(),
      text: tCommon('continue_v3_wechat'),
      icon: <WeeeIcon type="iconwechat" className="text-reserved-true-white !text-[34px] !leading-none" />,
      iconClass: 'bg-[#2dc100]'
    }
  ];

  const loginMethodSwitchItem =
    onLoginMethodSwitch &&
    (activeLoginMethod === 'email'
      ? {
          key: 'switch-phone',
          show: true,
          text: tCommon('continue_v3_phone'),
          icon: <PhoneFilled className="block shrink-0" aria-hidden />,
          onClick: onLoginMethodSwitch
        }
      : {
          key: 'switch-email',
          show: true,
          text: tCommon('continue_v3_email'),
          icon: <EnvelopeOutline className="block shrink-0" aria-hidden />,
          onClick: onLoginMethodSwitch
        });

  const renderPillItem = (item: {
    key: string;
    text: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    iconImg?: string;
  }) => (
    <div
      data-testid="wid-third-login-item"
      data-key={item.key}
      key={item.key}
      className={twMerge('relative h-[34px] box-border w-auto', itemBaseClass)}
      onClick={item.onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && item.onClick?.()}>
      {item.iconImg ? (
        <img
          data-testid="wid-third-login-item-icon"
          src={item.iconImg}
          alt=""
          className="h-[17px] w-[17px] shrink-0 object-contain"
        />
      ) : (
        <span
          data-testid="wid-third-login-item-icon"
          className="inline-flex shrink-0 items-center justify-center text-surface-100-fg-minor ">
          {item.icon}
        </span>
      )}
      <span data-testid="wid-third-login-item-text" className="leading-none">
        {item.text}
      </span>
    </div>
  );

  return (
    <>
      <div
        data-testid="wid-third-login-container"
        data-role="third-login"
        className={twMerge('w-full flex flex-wrap justify-center gap-2 my-2', className)}>
        {thirdMethods.map(item => {
          if (!item.show) return null;
          if (item.name === 'Google') {
            return (
              <div
                data-testid="wid-third-login-item-wrapper-google"
                data-key={item.name}
                key={item.rowKey}
                className="relative">
                <GoogleOauthLogin
                  className="absolute inset-0 cursor-pointer z-[1]"
                  redirectUrl={redirectUrl as string}
                  isLoginSignupPopup={isLoginSignupPopup}
                  onClick={handleGoogleClick}>
                  <span className="sr-only">Google OAuth Login</span>
                </GoogleOauthLogin>
                <div
                  data-testid="wid-third-login-item-google"
                  className={twMerge(itemBaseClass, 'pointer-events-none')}>
                  <img
                    data-testid="wid-third-login-item-google-icon"
                    src={item.iconImg}
                    alt={item.name}
                    className="h-[17px] w-[17px] shrink-0 object-contain"
                  />
                  <span data-testid="wid-third-login-item-google-text">{item.text}</span>
                </div>
              </div>
            );
          }
          return renderPillItem({
            key: item.rowKey,
            text: item.text,
            icon: item.icon,
            onClick: item.onClick,
            iconImg: item.iconImg
          });
        })}
        {loginMethodSwitchItem && renderPillItem(loginMethodSwitchItem)}
      </div>
      <Popup
        popupId="thirdLoginDeletedPopup"
        show={showDeletedPopup}
        position="center"
        wrapperClassName="w-11/12 rounded-600"
        onClose={() => setShowDeletedPopup(false)}>
        <div className="pb-8">
          <AccountDeletedContent popup onClose={() => setShowDeletedPopup(false)} />
        </div>
      </Popup>
    </>
  );
};

export default ThirdLoginV3;
